import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { NotificationsService } from '../../services/notifications.service';
import { PrepAwayAlertService } from '../../services/prep-away-alert.service';
import { PwaInstallService } from '../../services/pwa-install.service';
import { JwtService } from '../../services/jwt.service';
import { UsersService } from '../../services/users.service';
import { FilesService } from '../../services/files.service';
import { CurrentRestaurantService } from '../../services/current-restaurant.service';
import { TranslationService } from '../../services/translation.service';
import {
  PermissionsService,
  PermissionAction,
  PermissionModule,
} from '../../services/permissions.service';
import { EstablishmentPartnersService } from '../../services/establishment-partners.service';

export interface MenuItem {
  title: string;
  icon?: string | null;
  iconType?: 'nz-icon' | 'svg' | 'custom';
  iconPath?: string;
  routerLink?: string;
  children?: MenuItem[];
  cssClass?: string;
  /** Permission the user must have for this item to be navigable. When
   *  absent, the item is open to all authenticated users (self-service or
   *  cross-cutting page like home). When present and the user lacks
   *  it, the item stays visible in the sidebar with a lock icon — clicking
   *  shows a toast instead of navigating. */
  requires?: { module: PermissionModule; action: PermissionAction };
  /** Same lock, opened by any one of several rights (recipes: food or beverage). */
  requiresAny?: Array<{ module: PermissionModule; action: PermissionAction }>;
  /** French-only surface (NF525 signing or SUPER PDP e-invoicing) — fully
   *  hidden, not locked-with-toast, for a restaurant not on the NF525
   *  regime: it isn't a permission gap, the feature doesn't apply at all. */
  requiresNf525?: boolean;
  /** Only shown to an establishment linked to another one: unlinked, the feature does not exist. */
  requiresPartners?: boolean;
  /** Customer-facing sales (floor, orders, till, public menu): gone for a lab that does not sell to the public. */
  requiresPublicSales?: boolean;
  /** Compteur affiché à droite du libellé (0 = pas de pastille). */
  badge?: () => number;
}

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    NzButtonModule,
    NzSelectModule,
    TranslatePipe,
    NotificationBellComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  jwtService = inject(JwtService);
  usersService = inject(UsersService);
  filesService = inject(FilesService);
  message = inject(NzMessageService);
  currentRestaurant = inject(CurrentRestaurantService);
  private router = inject(Router);
  private translationService = inject(TranslationService);
  private permissions = inject(PermissionsService);
  private notifications = inject(NotificationsService);
  private prepAwayAlert = inject(PrepAwayAlertService);
  private establishmentPartners = inject(EstablishmentPartnersService);
  readonly hasInternalPartners = signal(false);

  constructor() {
    // Asked once a restaurant is known (none is on first paint) and again on every switch.
    toObservable(this.currentRestaurant.currentId)
      .pipe(
        filter((id): id is string => !!id),
        switchMap(() => this.establishmentPartners.partners()),
        takeUntilDestroyed()
      )
      .subscribe((partners) =>
        this.hasInternalPartners.set(partners.suppliers.length > 0 || partners.customers.length > 0)
      );
  }
  readonly pwaInstall = inject(PwaInstallService);
  readonly notifUnreadCount = this.notifications.unreadCount;

  @Input() isCollapsed = false;
  /** When false, the collapse/expand button is hidden — the sidebar is
   *  pinned open. Driven by `AppComponent.isOverlayMode` so the rail can
   *  still be toggled in tablet/overlay mode but never on wide desktop. */
  @Input() canToggle = true;
  @Input() width = '300px';
  @Input() logoUrl = 'assets/logo/yuzeat.png';
  @Input() logoTitle = 'YuZeat';
  @Input() logoLink = '#';

  @Output() isCollapsedChange = new EventEmitter<boolean>();

  readonly user = signal<any>(null);
  readonly restaurantPickerOpen = signal(false);
  readonly userMenuOpen = signal(false);
  readonly openSubmenuKey = signal<string | null>(null);
  readonly currentLanguage = signal<string>(localStorage.getItem('lang') ?? 'en');

  readonly availableLanguages = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
  ];

  ngOnInit() {
    // Apply localStorage language immediately for snappy first paint, then
    // resolve user/restaurant defaults asynchronously.
    const savedLang = localStorage.getItem('lang');
    if (savedLang) {
      this.translationService.setLanguage(savedLang);
      this.currentLanguage.set(savedLang);
    }
    this.loadCurrentUser();
    void this.currentRestaurant.load().then(() => {
      this.applyLanguageFallback();
      // Started here rather than in AppComponent: polling is pointless until
      // a restaurant is resolved, since the inbox is restaurant-scoped.
      this.notifications.startPolling();
      this.prepAwayAlert.start();
    });
  }

  loadCurrentUser() {
    if (!this.jwtService.isLogged()) {
      return;
    }
    this.usersService.getMe().subscribe({
      next: (u) => {
        this.user.set(u);
        // Feed the same payload into PermissionsService so every component
        // (sidebar lock checks, page-level button gates) reads from a
        // single source. Without this, `can()` would return false for
        // everyone — including CompanyAdmin/RestaurantAdmin who should
        // bypass — and the sidebar would lock every entry.
        this.permissions.setUser(u);
        this.applyLanguageFallback();
      },
      error: () => {
        this.user.set(null);
        this.permissions.setUser(null);
      },
    });
  }

  /** Language priority chain when no explicit user pick exists in localStorage:
   *  1. user.preferredLanguage (persisted in DB, follows user across devices)
   *  2. currentRestaurant.defaultLanguage (set when the restaurant is created)
   *  3. nothing — keep whatever the service has loaded (default 'en'). */
  private applyLanguageFallback(): void {
    if (localStorage.getItem('lang')) {
      return;
    } // user already picked, don't override
    const userLang = this.user()?.preferredLanguage as string | undefined;
    const restaurantLang = this.currentRestaurant.current?.defaultLanguage;
    const next = userLang || restaurantLang;
    if (next && next !== this.currentLanguage()) {
      this.translationService.setLanguage(next);
      this.currentLanguage.set(next);
      localStorage.setItem('lang', next);
    }
  }

  async onRestaurantChange(id: string) {
    if (this.currentRestaurant.currentId() === id) {
      return;
    }
    this.currentRestaurant.select(id);
    // Force re-creation of the current route component so all data is refetched
    // with the new restaurant scope.
    const currentUrl = this.router.url;
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    await this.router.navigateByUrl(currentUrl);
  }

  toggleRestaurantPicker(event: Event) {
    event.stopPropagation();
    this.restaurantPickerOpen.update((v) => !v);
    if (this.restaurantPickerOpen()) {
      this.userMenuOpen.set(false);
    }
  }

  toggleUserMenu(event: Event) {
    event.stopPropagation();
    this.userMenuOpen.update((v) => !v);
    if (this.userMenuOpen()) {
      this.restaurantPickerOpen.set(false);
    }
  }

  openProfileEdit() {
    this.userMenuOpen.set(false);
    const me = this.user();
    if (me?._id) {
      this.router.navigateByUrl(`/users/edit/${me._id}`);
    }
  }

  openNotifications() {
    this.userMenuOpen.set(false);
    this.router.navigate(['/notifications']);
  }

  async setLanguage(code: string) {
    if (code === this.currentLanguage()) {
      this.userMenuOpen.set(false);
      return;
    }
    this.currentLanguage.set(code);
    localStorage.setItem('lang', code);
    this.userMenuOpen.set(false);
    // Persist on the user record so the choice follows them across devices.
    // Best-effort, fire-and-forget — the reload below picks up the local
    // change immediately, the API call can race silently.
    if (this.jwtService.isLogged()) {
      this.usersService.updateMe({ preferredLanguage: code }).subscribe({
        error: () => undefined,
      });
    }
    // Full page reload — the only 100% reliable way to re-render every
    // pipe / nz-zorro placeholder / table column header in the new
    // language. The previous attempts (pure:false pipe + markForCheck,
    // route navigateByUrl trick) only refreshed the views that happened
    // to be in the CD path of the click event, leaving forms and lists
    // in passive sub-trees stuck on EN. The TranslationService
    // constructor now reads localStorage so the post-reload boot
    // immediately loads `fr.json` — no EN flash.
    window.location.reload();
  }

  isSubmenuOpen(item: MenuItem): boolean {
    return this.openSubmenuKey() === this.submenuKey(item);
  }

  onSubmenuOpenChange(item: MenuItem, isOpen: boolean): void {
    const key = this.submenuKey(item);
    if (isOpen) {
      this.openSubmenuKey.set(key);
    } else if (this.openSubmenuKey() === key) {
      this.openSubmenuKey.set(null);
    }
  }

  private submenuKey(item: MenuItem): string {
    return item.routerLink || item.title;
  }

  async selectRestaurant(id: string) {
    this.restaurantPickerOpen.set(false);
    await this.onRestaurantChange(id);
    await this.notifications.refresh();
  }

  formatRestaurantAddress(
    r: { address?: { street?: string; city?: string; zipCode?: string } } | null | undefined
  ): string | null {
    const a = r?.address;
    if (!a) {
      return null;
    }
    const parts = [a.street, [a.zipCode, a.city].filter(Boolean).join(' ')].filter(
      (p) => p && p.trim()
    );
    return parts.length ? parts.join(', ') : null;
  }

  @HostListener('document:click')
  closeOverlays() {
    if (this.restaurantPickerOpen()) {
      this.restaurantPickerOpen.set(false);
    }
    if (this.userMenuOpen()) {
      this.userMenuOpen.set(false);
    }
  }

  get userFirstName(): string {
    return this.user()?.firstName ?? '';
  }

  get userLastName(): string {
    return this.user()?.lastName ?? '';
  }

  get userPhoto(): string {
    const photo = this.user()?.photo;
    const id = typeof photo === 'string' ? photo : photo?._id;
    return id ? this.filesService.fileContentUrl(id) : '';
  }

  menuItems: MenuItem[] = [
    {
      title: 'menu.home',
      iconType: 'nz-icon',
      icon: 'home',
      routerLink: '/home',
      cssClass: 'home-menu-item',
      // No `requires`: home is open to every authenticated user.
    },
    {
      title: 'menu.dashboard',
      iconType: 'svg',
      iconPath: 'assets/icons/dashboard.svg',
      routerLink: '/dashboard',
      cssClass: 'dashboard-menu-item',
      requires: { module: 'DASHBOARD', action: 'READ' },
    },
    {
      title: 'menu.kitchenFlow',
      iconType: 'svg',
      iconPath: 'assets/icons/recipes-ingredients.svg',
      cssClass: 'recipes-menu-item',
      children: [
        {
          title: 'menu.recipes',
          routerLink: '/recipes',
          iconType: 'svg',
          iconPath: 'assets/icons/recipes.svg',
          cssClass: 'kitchen-child-item',
          requiresAny: [
            { module: 'RECIPES_FOOD', action: 'READ' },
            { module: 'RECIPES_BEVERAGE', action: 'READ' },
          ],
        },
        {
          title: 'menu.ingredients',
          routerLink: '/ingredients',
          iconType: 'svg',
          iconPath: 'assets/icons/ingredient.svg',
          cssClass: 'kitchen-child-item',
          requires: { module: 'ITEMS', action: 'READ' },
        },
        {
          title: 'menu.production',
          routerLink: '/production',
          icon: 'fire',
          iconType: 'nz-icon',
          cssClass: 'kitchen-child-item',
          requires: { module: 'PRODUCTION', action: 'READ' },
        },
      ],
    },
    {
      title: 'menu.sales',
      iconType: 'svg',
      iconPath: 'assets/icons/sales.svg',
      cssClass: 'sales-menu-item',
      children: [
        {
          title: 'menu.products',
          routerLink: '/products',
          iconType: 'svg',
          iconPath: 'assets/icons/products.svg',
          cssClass: 'sales-child-item',
          requires: { module: 'PRODUCT', action: 'READ' },
        },
        {
          title: 'menu.optionGroups',
          routerLink: '/option-groups',
          icon: 'control',
          iconType: 'nz-icon',
          cssClass: 'sales-child-item',
          requires: { module: 'PRODUCT', action: 'READ' },
        },
        {
          title: 'menu.menu',
          requiresPublicSales: true,
          routerLink: '/menu',
          iconType: 'svg',
          iconPath: 'assets/icons/menu.svg',
          cssClass: 'sales-child-item',
          requires: { module: 'MENU', action: 'READ' },
        },
      ],
    },
    {
      title: 'menu.stockManagement',
      iconType: 'svg',
      iconPath: 'assets/icons/stock.svg',
      cssClass: 'stock-menu-item',
      children: [
        {
          title: 'menu.stockOverview',
          routerLink: '/stock-overview',
          iconType: 'svg',
          iconPath: 'assets/icons/overview.svg',
          cssClass: 'stock-child-item',
          requires: { module: 'STOCK', action: 'READ' },
        },
        {
          title: 'menu.stockHistory',
          routerLink: '/stock-history',
          iconType: 'svg',
          iconPath: 'assets/icons/history.svg',
          cssClass: 'stock-child-item',
          requires: { module: 'STOCK', action: 'READ' },
        },
        {
          title: 'menu.suppliers',
          routerLink: '/suppliers',
          iconType: 'svg',
          iconPath: 'assets/icons/supplier.svg',
          cssClass: 'stock-child-item',
          requires: { module: 'SUPPLIERS', action: 'READ' },
        },
        {
          title: 'menu.purchasing',
          routerLink: '/purchasing',
          icon: 'shopping-cart',
          iconType: 'nz-icon',
          cssClass: 'stock-child-item',
          requires: { module: 'SUPPLIERS', action: 'READ' },
        },
        {
          title: 'menu.internalOrders',
          routerLink: '/internal-orders',
          icon: 'swap',
          iconType: 'nz-icon',
          cssClass: 'stock-child-item',
          requires: { module: 'STOCK', action: 'READ' },
          requiresPartners: true,
        },
      ],
    },
    {
      title: 'menu.service',
      requiresPublicSales: true,
      iconType: 'svg',
      iconPath: 'assets/icons/floor-plan.svg',
      cssClass: 'service-menu-item',
      children: [
        {
          title: 'menu.mapEditor',
          routerLink: '/map-editor',
          iconType: 'svg',
          iconPath: 'assets/icons/floor-plan.svg',
          cssClass: 'service-child-item',
          requires: { module: 'FLOOR_PLAN', action: 'READ' },
        },
        {
          title: 'menu.reservations',
          routerLink: '/reservations',
          icon: 'calendar',
          iconType: 'nz-icon',
          cssClass: 'service-child-item',
          requires: { module: 'RESERVATION', action: 'READ' },
        },
        {
          title: 'menu.orders',
          routerLink: '/orders',
          icon: 'profile',
          iconType: 'nz-icon',
          cssClass: 'service-child-item',
          requires: { module: 'ORDER', action: 'READ' },
        },
        {
          title: 'menu.orderPreparation',
          routerLink: '/order-preparation',
          icon: 'fire',
          iconType: 'nz-icon',
          cssClass: 'service-child-item',
          requires: { module: 'ORDER', action: 'PREPARE' },
        },
      ],
    },
    {
      title: 'menu.staffManagement',
      iconType: 'svg',
      iconPath: 'assets/icons/user.svg',
      cssClass: 'staff-management-menu-item',
      children: [
        {
          title: 'menu.users',
          routerLink: '/users',
          iconType: 'svg',
          iconPath: 'assets/icons/user.svg',
          cssClass: 'staff-management-child-item',
          requires: { module: 'USER', action: 'READ' },
        },
        {
          title: 'menu.planning',
          routerLink: '/staff-schedule',
          iconType: 'svg',
          iconPath: 'assets/icons/schedule.svg',
          cssClass: 'staff-management-child-item',
          requires: { module: 'STAFF_SCHEDULE', action: 'READ' },
        },
        {
          title: 'menu.myPlanning',
          routerLink: '/my-planning',
          iconType: 'svg',
          iconPath: 'assets/icons/schedule.svg',
          cssClass: 'staff-management-child-item',
          // Self-service: every employee sees their own planning.
        },
        {
          title: 'menu.tasks',
          routerLink: '/tasks',
          icon: 'check-square',
          iconType: 'nz-icon',
          cssClass: 'staff-management-child-item',
          requires: { module: 'TASKS', action: 'READ' },
        },
        {
          title: 'menu.myTasks',
          routerLink: '/my-tasks',
          icon: 'unordered-list',
          iconType: 'nz-icon',
          cssClass: 'staff-management-child-item',
          // Self-service: every employee sees their own tasks.
        },
      ],
    },
    {
      title: 'menu.fiscal',
      requiresPublicSales: true,
      icon: 'safety-certificate',
      iconType: 'nz-icon',
      cssClass: 'fiscal-menu-item',
      children: [
        {
          title: 'menu.cashRegister',
          routerLink: '/cash-register',
          icon: 'wallet',
          iconType: 'nz-icon',
          cssClass: 'fiscal-child-item',
          requires: { module: 'CASH_FISCAL', action: 'READ' },
        },
        {
          title: 'menu.closures',
          routerLink: '/closures',
          icon: 'reconciliation',
          iconType: 'nz-icon',
          cssClass: 'fiscal-child-item',
          requires: { module: 'CASH_FISCAL', action: 'READ' },
        },
        {
          title: 'menu.corrections',
          routerLink: '/corrections',
          icon: 'rollback',
          iconType: 'nz-icon',
          cssClass: 'fiscal-child-item',
          requires: { module: 'CASH_FISCAL', action: 'READ' },
          requiresNf525: true,
        },
        {
          title: 'menu.fiscalJournal',
          routerLink: '/fiscal-journal',
          icon: 'file-protect',
          iconType: 'nz-icon',
          cssClass: 'fiscal-child-item',
          requires: { module: 'CASH_FISCAL', action: 'READ' },
          requiresNf525: true,
        },
      ],
    },
    {
      title: 'menu.legalWatch',
      icon: 'audit',
      iconType: 'nz-icon',
      routerLink: '/legal-watch',
      cssClass: 'legal-watch-menu-item',
      // No `requires`: la veille juridique est ouverte à tout le staff.
    },
    {
      title: 'menu.settings',
      iconType: 'svg',
      iconPath: 'assets/icons/settings.svg',
      cssClass: 'settings-menu-item',
      children: [
        {
          title: 'menu.roles',
          routerLink: '/roles',
          iconType: 'svg',
          iconPath: 'assets/icons/role.svg',
          cssClass: 'settings-child-item',
          requires: { module: 'ROLES', action: 'READ' },
        },
        {
          title: 'menu.categories',
          routerLink: '/categories',
          iconType: 'svg',
          iconPath: 'assets/icons/categories.svg',
          cssClass: 'settings-child-item',
          requires: { module: 'CATEGORIES', action: 'READ' },
        },
        {
          title: 'menu.menuSettings',
          requiresPublicSales: true,
          routerLink: '/menu-settings',
          iconType: 'svg',
          iconPath: 'assets/icons/menu-settings.svg',
          cssClass: 'settings-child-item',
          requires: { module: 'MENU', action: 'READ' },
        },
        {
          title: 'menu.alertSettings',
          routerLink: '/alert-settings',
          icon: 'bell',
          iconType: 'nz-icon',
          requires: { module: 'RESTAURANT_SETTINGS', action: 'READ' },
        },
        {
          title: 'menu.financialSettings',
          routerLink: '/financial-settings',
          icon: 'calculator',
          iconType: 'nz-icon',
          requires: { module: 'RESTAURANT_SETTINGS', action: 'READ' },
        },
        {
          title: 'menu.printingSettings',
          routerLink: '/printing-settings',
          icon: 'printer',
          iconType: 'nz-icon',
          requires: { module: 'RESTAURANT_SETTINGS', action: 'READ' },
        },
        {
          title: 'menu.kitchenSettings',
          routerLink: '/kitchen-settings',
          icon: 'history',
          iconType: 'nz-icon',
          requires: { module: 'RESTAURANT_SETTINGS', action: 'READ' },
        },
        {
          title: 'menu.eInvoicing',
          routerLink: '/e-invoicing',
          icon: 'global',
          iconType: 'nz-icon',
          requires: { module: 'RESTAURANT_SETTINGS', action: 'READ' },
          requiresNf525: true,
        },
        {
          title: 'menu.restaurantSchedule',
          routerLink: '/restaurant-schedule',
          iconType: 'svg',
          iconPath: 'assets/icons/schedule.svg',
          cssClass: 'settings-child-item',
          requires: { module: 'RESTAURANT_SETTINGS', action: 'READ' },
        },
      ],
    },
  ];

  /** `menuItems` filtered to what this restaurant's fiscal regime allows —
   *  fully removed, not locked-with-toast: a restaurant not on NF525 isn't
   *  missing a permission, the feature just doesn't apply. A parent group
   *  left with no children (all of them NF525-only) is dropped too. */
  get visibleMenuItems(): MenuItem[] {
    return this.filterByFiscalRegime(this.menuItems);
  }

  private filterByFiscalRegime(items: MenuItem[]): MenuItem[] {
    const requiresNf525 = this.currentRestaurant.requiresNf525;
    const linked = this.hasInternalPartners();
    const sellsToPublic = this.currentRestaurant.sellsToPublic;
    return items
      .filter((item) => requiresNf525 || !item.requiresNf525)
      .filter((item) => linked || !item.requiresPartners)
      .filter((item) => sellsToPublic || !item.requiresPublicSales)
      .map((item) =>
        item.children ? { ...item, children: this.filterByFiscalRegime(item.children) } : item
      )
      .filter((item) => !item.children || item.children.length > 0);
  }

  /** True when the current user can't navigate to this item. Parent
   *  groups (no `requires`, with `children`) are locked only when ALL
   *  children are locked — they stay accessible to expand otherwise. */
  isLocked(item: MenuItem): boolean {
    if (item.requires) {
      return !this.permissions.can(item.requires.module, item.requires.action);
    }
    if (item.requiresAny) {
      return !item.requiresAny.some((right) => this.permissions.can(right.module, right.action));
    }
    if (item.children?.length) {
      return item.children.every((child) => this.isLocked(child));
    }
    return false;
  }

  /** Triggered when the user clicks a locked item. We swallow the
   *  navigation and surface a toast hint instead of letting them land on
   *  a 403 page. */
  onLockedClick(item: MenuItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const required = item.requires ?? item.requiresAny?.[0];
    const moduleKey = required?.module ?? '';
    const actionKey = required?.action ?? '';
    const detail = moduleKey && actionKey ? `${moduleKey}.${actionKey}` : '';
    this.message.warning(
      detail
        ? `${this.translationService.get('sidebar.lockedHint')} (${detail})`
        : this.translationService.get('sidebar.lockedHint')
    );
  }

  onCollapsedChange(collapsed: boolean) {
    this.isCollapsed = collapsed;
    this.isCollapsedChange.emit(collapsed);
  }

  /** Manual toggle for the collapse button in the sidebar header. Auto-collapse
   *  on narrow viewports lives in `AppComponent` (HostListener on resize). */
  toggleCollapsed() {
    this.onCollapsedChange(!this.isCollapsed);
  }

  logout() {
    this.currentRestaurant.reset();
    this.jwtService.endEmployeeSession();
  }

  async installApp(): Promise<void> {
    this.userMenuOpen.set(false);
    await this.pwaInstall.promptInstall();
  }
}
